import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {animate, sequence, style, transition, trigger} from '@angular/animations';

import {filter, Observable, Subscription, switchMap, tap} from 'rxjs';

import {LatLng} from 'leaflet';

import {PanelNavigationService} from '@commons/services/navigation/panel-navigation.service';
import {VehicleService} from '@commons/services/vehicle/vehicle.service';
import {Vehicle} from '../../../commons/models/domain/Vehicle';
import {MapService} from '@commons/services/map/map/map.service';
import {MapConfigService} from '@commons/services/map/config/map-config.service';
import {VehicleDetailsTabsComponent} from './tabs/vehicle-details-tabs.component';
import {NewWebSocketHttpService} from "@shared/websocket/new-web-socket-http.service";

@Component({
    selector: 'tk-vehicle-details',
    styleUrls: ['./vehicle-details.component.less'],
    templateUrl: './vehicle-details.component.html',
    animations: [
        trigger('openClose', [
            transition(":enter", [sequence([style({ left: '416px', 'z-index': 0 }), animate('0.1s', style({ transform: 'translateX(-320px)', 'z-index': 400 }) )] )]),
            transition(":leave", [sequence([style({ left: '416px', 'z-index': 0 }), animate('0.1s', style({ left: '56px' }) )] )]),
        ])
    ]
})
export class VehicleDetailsComponent implements OnInit, OnDestroy {

    /**
     * Flag indicating whether this component is visible
     */
    public isVisible = false;

    /**
     * flaga ladowania
     */
    public isLoading = true;

    /**
     * Vehicle to show
     */
    public vehicle?: Vehicle;

    public vehicleId?: number;

    /**
    * Subscription to url changes stream
    */
    private navSub: Subscription;


    /**
     * Sub for switch follow vehicle status
     */
    private followSub: Subscription;


    /**
     * obserwacja zmian
     */
    private vehicleChangeSub?: Subscription;

    /**
     * kalsy dla zakladki
     */
    public styleClass: Array<string> = [];


    /**
     * czy podazac za pojazdem
     */
    private followVehicle = true;

    /**
     * pierwszy zoom po otwarciu
     */
    private isFirstZoom = true;

    private ComponentSubsc: Subscription = new Subscription();

    /**
    *
    * @param $nav
    * @param $vehicle
    * @param $activatedRoute
    */
    constructor(
        private $nav: PanelNavigationService,
        private $vehicleService: VehicleService,
        private $activatedRoute: ActivatedRoute,
        private $map: MapService,
        private $mapConfig: MapConfigService,
        private newWebSocketHttpService: NewWebSocketHttpService
    ) {

        this.isLoading = true;

        // Build stream of url changes
        this.navSub = this.$nav.getVisibilitySubject().pipe(
            tap(result => {
                if (this.vehicleChangeSub) {
                    this.vehicleChangeSub.unsubscribe();
                }
            }),
            filter(item => (
                !item.subsites
                || item.subsites.indexOf('vehicle-details') !== -1
                || item.subsites.indexOf('vehicle-details-only') !== -1
            )))
            .subscribe(change => {
                this.isVisible = change.show;
                this.styleClass = (change.subsites || []).map((i: any) => {
                  switch (i) {
                    case 'vehicle-details-only':
                      return 'left-first-level-container';
                    default:
                      return 'left-second-level-container';
                  }
                });

                const vid = parseInt(change.id, 10);
                this.vehicleId = vid;
                this.$mapConfig.setCurrentVehicleId('tk-vehicle-details', this.vehicleId);
                this.isFirstZoom = true;

                const sub = this.$vehicleService.getDetails$(this.vehicleId).subscribe(v => {
                  this.vehicle = v;
                  this.followVehicle = true;
                  this.goToVehicleLocation('vehicle -> details -> getVehicleData');
                  this.$vehicleService.setFollowVehicleStatus(true);
                  this.subscribeForChanges();
                  this.isLoading = false;
                  setTimeout(() => this.adjustContentHeight(), 100);
                });
                this.ComponentSubsc.add(sub);
            });

        // Listinig for changes of follow vehicle status
        this.followSub = this.$vehicleService
            .getFollowVehicleSubject()
            .subscribe(status => {
                setTimeout(() => {
                    if (this.followVehicle !== status) {
                        this.followVehicle = status;
                        if (this.followVehicle) {
                            this.goToVehicleLocation('vehicle -> details -> getFollowVehicleSubject');
                        }
                    }
                }, 0);
            });

    }

    private subscribeForChanges() {
      // odwiazanie ewentualnej istniejacje
      if (this.vehicleChangeSub) {
        this.vehicleChangeSub.unsubscribe();
      }

      this.vehicleChangeSub = this.newWebSocketHttpService.updatedVehicles.subscribe(vehiclesList => {
        const current = vehiclesList.find(v => v.Id === this.vehicleId);

        if (!current)
          return;

        this.vehicle = {...current} as Vehicle;

        // console.log('CHANGES', this.vehicle);

        this.goToVehicleLocation('vehicle -> details -> OnVehicleDataChangeNotification');
        this.isLoading = false;
        setTimeout(() => this.adjustContentHeight(), 100);
      });
    }

    public ngOnInit(): void {
    }

    public ngOnDestroy(): void {
        this.ComponentSubsc.unsubscribe();
        if (this.navSub) {
            this.navSub.unsubscribe();
        }
        if (this.vehicleChangeSub) {
            this.vehicleChangeSub.unsubscribe();
        }
        if (this.followSub) {
            this.followSub.unsubscribe();
        }
        this.followVehicle = false;
    }

    /**
 * mapa
 */
    private goToVehicleLocation(source?: string): void {
        if (!this.followVehicle) return;

        if (this.vehicle && this.vehicle.LL) {

            const _lat = parseFloat(this.vehicle.LL.Lt + '');
            const _lng = parseFloat(this.vehicle.LL.Ln + '');

            if (_lat && _lat !== 361 && _lng && _lng !== 361) {
                const lat = (_lat);
                const lng = (_lng); // - 0.001);
                const coords = new LatLng(lat, lng);
                this.$map.showPointWithZoom(coords);
                this.isFirstZoom = false;
            }
        }
    }

    @ViewChild('header', {read: ElementRef }) private headerElement?: ElementRef;
    @ViewChild(VehicleDetailsTabsComponent) private tabsElement?: VehicleDetailsTabsComponent;

    private adjustContentHeight(): void {
        if (this.headerElement && this.tabsElement) {
            const headerChildNodes = Array.from<any>(this.headerElement.nativeElement.childNodes);
            const vehicleDetailsHeader = headerChildNodes.find(x => x.className.toString().toLowerCase().includes('vehicle-details-header'));
            const headerHeight = vehicleDetailsHeader.offsetHeight;
            const headerOffset = vehicleDetailsHeader.offsetTop;
            const margin = 15;
            this.tabsElement.setTop(headerHeight + headerOffset + margin);
        }
    }
}
